import { OpportunityCard, OpportunityCardContent } from './OpportunityCard';
import { DollarSign } from 'lucide-react';
import { DataTable } from './DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import {
  EngagementSheetPrimaryBtn,
  OpportunityEngagementSheet,
} from '@/components/OpportunityEngagementSheet';
import { useOpportunityState } from '@/pages/AdvisorDashboardOpportunities';
import { createOpportunityClientROIProjectionTableColumns } from './opportunityClientROIProjectionTable';
import { OpportunityAIUnderwritingGuidanceSheet } from './OpportunityAIUnderWritingGuidanceSheet';
import { SendOpportunityMessageModal } from './SendOpportunityMessageModal';
import { reviewROIOpportunityReminderNoLinkTemplate } from '@shared';

export function OpportunityQaAClientROIProjection() {
  const {
    advisorOpportunitiesState,
    status,
    priorityStatus,
    qualifyingClients,
    clientsEligibleForReminder,
  } = useOpportunityState('QAA_ROI_PROJECTION_LINKS');
  const [sheetOpen, setSheetOpen] = useState(false);

  const highestROI = useMemo(() => {
    let roi = -1;
    advisorOpportunitiesState.qualifyingClients.forEach(client => {
      if (client.quoteProjection?.roi && client.quoteProjection.roi > roi) {
        roi = client.quoteProjection.roi;
      }
    });
    return roi * 100;
  }, [advisorOpportunitiesState]);

  const engagementSheet = useMemo(
    () => (
      <OpportunityResendClientMagicLinksEngagementSheet
        sheetOpen={sheetOpen}
        setSheetOpen={setSheetOpen}
        qualifyingClients={advisorOpportunitiesState.qualifyingClients}
        buttonTrigger={
          <EngagementSheetPrimaryBtn
            priorityStatus={priorityStatus}
            onClick={() => setSheetOpen(true)}
            actionText={`View Clients`}
          />
        }
      />
    ),
    [sheetOpen, setSheetOpen, qualifyingClients, clientsEligibleForReminder, priorityStatus],
  );

  if (priorityStatus === 'Not applicable') return null;
  return (
    <OpportunityCard
      priorityStatus={priorityStatus}
      isLoading={status === 'fetchingClients'}
      cardContent={
        <OpportunityCardContent
          icon={
            <DollarSign
              className="h-8 w-8"
              strokeWidth={1}
            />
          }
          primaryMetric={qualifyingClients ? qualifyingClients.length.toString() : '0'}
          metricDescription={`Client${qualifyingClients.length > 1 ? 's' : ''} who ${qualifyingClients.length > 1 ? 'are' : 'is'} likely eligible for policies with modeled ROI as high as ${highestROI.toFixed(0)}%`}
        />
      }
      cardFooterContent={engagementSheet}
    />
  );
}

function useEmailTemplateState(advisor: Advisor) {
  const [advisorProvidedBody, setAdvisorProvidedBody] = useState<string | undefined>(undefined);
  const [isEditMessageOpen, setIsEditMessageOpen] = useState(false);

  const handleUpdateMessage = (body: string) => {
    setAdvisorProvidedBody(body);
    setIsEditMessageOpen(false);
  };

  return {
    advisorProvidedBody,
    isEditMessageOpen,
    setIsEditMessageOpen,
    handleUpdateMessage,
  };
}

function OpportunityResendClientMagicLinksEngagementSheet({
  sheetOpen,
  setSheetOpen,
  qualifyingClients,
  buttonTrigger,
}: {
  sheetOpen: boolean;
  setSheetOpen: (open: boolean) => void;
  qualifyingClients: Client[];
  buttonTrigger: React.ReactNode;
}) {
  const { advisor, clientsIsLoading, status } = useOpportunityState('QAA_ROI_PROJECTION_LINKS');
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const { advisorProvidedBody, isEditMessageOpen, setIsEditMessageOpen } =
    useEmailTemplateState(advisor);

  useEffect(() => {
    if (!sheetOpen) {
      setSelectedClients([]);
    }
  }, [sheetOpen]);

  const [openUnderwritingAnalysis, setOpenUnderwritingAnalysis] = useState(false);
  const [underWritingResult, setUnderWritingResult] = useState<{
    eligibilityOverview: {
      status: string;
      content: string[];
    };
    flaggedItems: string[];
    recommendations: string[];
  } | null>(null);

  const isLoading = status === 'sendingEmails';

  const handleViewAIUnderwritingGuidance = (client: Client) => {
    setUnderWritingResult(client?.quoteProjection?.aiUnderwritingAnalysis || null);
    setOpenUnderwritingAnalysis(true);
  };

  const closeAIUnderwritingGuidance = () => {
    setUnderWritingResult(null);
    setOpenUnderwritingAnalysis(false);
  };

  const memoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        key={qualifyingClients.map(c => c.clientId).join('-')}
        columns={
          createOpportunityClientROIProjectionTableColumns({
            onViewDetailsClick: handleViewAIUnderwritingGuidance,
          }) as ColumnDef<Client>[]
        }
        getRowId={row => row.clientId}
        data={qualifyingClients}
        isLoading={clientsIsLoading}
        className="mb-12"
        tableId="qaaRoiTable"
        variant="outlined"
        tableSkeletonRows={10}
        defaultSelection={qualifyingClients}
        pageSize={8}
        hideColumnSelector={true}
        onSelectionChange={setSelectedClients}
        isRowDeactivated={row => !row.clientEmail}
      />
    ),
    [qualifyingClients, clientsIsLoading],
  );

  const description = useMemo(() => {
    let roi = -1;
    qualifyingClients.forEach(client => {
      if (client.quoteProjection?.roi && client.quoteProjection.roi > roi) {
        roi = client.quoteProjection.roi;
      }
    });

    roi = roi * 100;

    return `You have ${qualifyingClients.length} client${qualifyingClients.length > 1 ? 's' : ''} who ${qualifyingClients.length > 1 ? 'are' : 'is'} likely eligible for policies with modeled ROI as high as ${roi?.toFixed(2)}%`;
  }, [qualifyingClients]);

  return (
    <>
      <OpportunityEngagementSheet
        trigger={buttonTrigger}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="Eligible Clients with Highest ROI"
        description={description}
        clientsTable={memoizedDataTable}
        floatingMenuCtaProps={{
          text: `Review and send message`,
          onClick: () => setIsEditMessageOpen(true),
          disabled: isLoading,
        }}
        selectedClientCount={selectedClients.length}
      />
      <OpportunityAIUnderwritingGuidanceSheet
        parsedResults={underWritingResult}
        isOpen={openUnderwritingAnalysis}
        onClose={closeAIUnderwritingGuidance}
      />

      <SendOpportunityMessageModal
        isOpen={isEditMessageOpen}
        onClose={() => setIsEditMessageOpen(false)}
        advisor={advisor}
        defaultMessage={advisorProvidedBody}
        emailTemplate={reviewROIOpportunityReminderNoLinkTemplate}
        emailTemplateProps={{
          clientName: '{{Client Name}}',
          advisorEmail: advisor.advisorEmail,
          schedulingMeetingLink: advisor.schedulingLinkUrl,
          emailPreferencesLink: '',
          isPreview: true,
        }}
        selectedClients={selectedClients}
        engagementType="QAA_ROI_PROJECTION_LINKS"
      />
    </>
  );
}
